import { useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { getProductosPublic } from "../../services/public/ProductosPublic";
import { useStoreLoading } from "../../context/StoreLoadingContext";
import ProductGrid from "../../components/store/ProductGrid";
import "./Store.css";

const PAGE_SIZE = 20;

function StoreHome() {
  const [products, setProducts] = useState([]);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const offsetRef = useRef(0);

  // Numero de la peticion en curso. Sirve para descartar la respuesta de una
  // consulta vieja que llega despues de otra mas nueva: sin esto, cambiar de
  // filtro dos veces rapido podia dejar en pantalla el resultado del
  // primero.
  const peticionRef = useRef(0);

  const navigate = useNavigate();
  const { beginTask } = useStoreLoading();
  const primeraCargaRef = useRef(true);
  const ctx = useOutletContext() || {};
  const search = String(ctx.search ?? "");
  const category = ctx.category ?? null;
  const brand = ctx.brand ?? null;

  const normalizeResponse = (data) => {
    if (data && Array.isArray(data.items)) {
      return {
        items: data.items,
        hasMore:
          typeof data.hasMore === "boolean"
            ? data.hasMore
            : data.items.length === PAGE_SIZE,
      };
    }
    if (data && Array.isArray(data.data)) {
      return {
        items: data.data,
        hasMore:
          typeof data.hasMore === "boolean"
            ? data.hasMore
            : data.data.length === PAGE_SIZE,
      };
    }
    if (Array.isArray(data)) {
      return { items: data, hasMore: data.length === PAGE_SIZE };
    }
    return { items: [], hasMore: false };
  };

  const loadPage = async ({ reset = false } = {}) => {
    // Un reset (cambio de filtro) siempre se dispara, aunque haya otra
    // consulta en curso. Antes se descartaba, y como el efecto ya habia
    // vaciado la lista, quedaba "No results" para siempre: pasaba al entrar
    // por url a /store?category=N y tambien al tocar un filtro mientras la
    // primera pagina todavia cargaba. Lo que se bloquea es pedir mas
    // paginas de lo mismo dos veces.
    if (!reset && status === "loading") return;
    if (!reset && !hasMore) return;

    const miPeticion = ++peticionRef.current;

    try {
      setStatus("loading");
      setError(null);

      const nextOffset = reset ? 0 : offsetRef.current;

      const data = await getProductosPublic({
        limit: PAGE_SIZE,
        offset: nextOffset,
        search,
        category,
        brand,
      });

      // Llego tarde: ya hay otra consulta mas nueva y esta respuesta es de
      // un filtro que el usuario ya cambio.
      if (miPeticion !== peticionRef.current) return;

      const { items, hasMore: more } = normalizeResponse(data);

      setProducts((prev) => (reset ? items : [...prev, ...items]));
      setHasMore(more);
      offsetRef.current = nextOffset + items.length;

      setStatus("ok");
    } catch (err) {
      if (miPeticion !== peticionRef.current) return;
      console.error("Error loading products:", err);
      setError(err);
      setStatus("error");
    }
  };

  useEffect(() => {
    setProducts([]);
    setHasMore(true);
    offsetRef.current = 0;

    // Solo la carga inicial retiene la pantalla de bienvenida; los
    // cambios de filtro ya muestran sus propios skeletons.
    if (primeraCargaRef.current) {
      primeraCargaRef.current = false;
      const finTarea = beginTask();
      loadPage({ reset: true }).finally(finTarea);
    } else {
      loadPage({ reset: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, category, brand]);

  const hasFilters = Boolean(search.trim() || category || brand);

  return (
    <div className="sth-scope">
      <div className="sth-home">
        <h2 className="sth-title">Products</h2>

        {status === "error" && (
          <div className="sth-state sth-state--error">
            Failed to load products.
            <div className="sth-error-msg">
              {String(error?.response?.data?.message || error?.message || "")}
            </div>
          </div>
        )}

        {status !== "error" && (
          <ProductGrid
            products={products}
            loading={status === "loading" && products.length === 0}
            skeletonCount={12}
            onSelect={(p) => navigate(`/store/product/${p.producto_id}`)}
          />
        )}

        {status === "ok" && products.length === 0 && (
          <div className="sth-state">
            {hasFilters
              ? "No results with those filters."
              : "No products available yet."}
          </div>
        )}

        {products.length > 0 && hasMore && (
          <div className="sth-load-more-wrap">
            <button
              type="button"
              className="sth-load-more"
              onClick={() => loadPage()}
              disabled={status === "loading"}
            >
              {status === "loading" ? "Loading..." : "Load more"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default StoreHome;
